import React, { useEffect, useState, useCallback } from 'react';
import { Plus, ShoppingCart, Trash2, Edit2, Check, X as XIcon, Truck, FileDown } from 'lucide-react';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/authStore';
import { format } from 'date-fns';

export default function Procurement() {
  const { user } = useAuthStore();
  const canManage = ['admin', 'finance', 'superadmin'].includes(user?.role || '');
  const [tab, setTab] = useState('requisitions');

  return (
    <div className="fade-in">
      <div className="page-header">
        <div><h1 className="page-title">Procurement</h1><p className="page-subtitle">Requisitions, purchase orders and vendors</p></div>
      </div>
      <div className="tabs">
        <button className={`tab ${tab === 'requisitions' ? 'active' : ''}`} onClick={() => setTab('requisitions')}>Requisitions</button>
        {canManage && <button className={`tab ${tab === 'orders' ? 'active' : ''}`} onClick={() => setTab('orders')}>Purchase Orders</button>}
        {canManage && <button className={`tab ${tab === 'vendors' ? 'active' : ''}`} onClick={() => setTab('vendors')}>Vendors</button>}
      </div>
      {tab === 'requisitions' && <Requisitions canManage={canManage} />}
      {tab === 'orders' && canManage && <PurchaseOrders />}
      {tab === 'vendors' && canManage && <Vendors />}
    </div>
  );
}

const statusColor: Record<string, string> = {
  pending: 'warning', approved: 'success', rejected: 'danger', converted: 'info',
  draft: 'gray', sent: 'info', partially_received: 'warning', received: 'success', cancelled: 'danger',
};

function ItemRows({ items, setItems, costField }: { items: any[]; setItems: (i: any[]) => void; costField: string }) {
  const update = (i: number, k: string, v: any) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const add = () => setItems([...items, { item_name: '', quantity: 1, [costField]: '' }]);
  const remove = (i: number) => setItems(items.filter((_, idx) => idx !== i));

  return (
    <div>
      <label className="form-label">Items *</label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((it, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 80px 1fr auto', gap: 8, alignItems: 'center' }}>
            <input className="form-input" placeholder="Item name" value={it.item_name} onChange={e => update(i, 'item_name', e.target.value)} />
            <input type="number" min={1} className="form-input" placeholder="Qty" value={it.quantity} onChange={e => update(i, 'quantity', parseInt(e.target.value) || 1)} />
            <input type="number" min={0} step="0.01" className="form-input" placeholder="Unit cost" value={it[costField]} onChange={e => update(i, costField, e.target.value)} />
            <button type="button" onClick={() => remove(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)' }}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      <button type="button" className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={add}><Plus size={13} />Add Item</button>
    </div>
  );
}

function Requisitions({ canManage }: { canManage: boolean }) {
  const [reqs, setReqs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [showModal, setShowModal] = useState(false);

  const fetchReqs = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/procurement/requisitions', { params: filter ? { status: filter } : {} }); setReqs(r.data.data.requisitions || []); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchReqs(); }, [fetchReqs]);

  const act = async (id: string, action: 'approve' | 'reject') => {
    try { await api.patch(`/procurement/requisitions/${id}/${action}`); toast.success(`Requisition ${action}d`); fetchReqs(); }
    catch (err: any) { toast.error(err?.response?.data?.message || 'Action failed'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          {[['', 'All'], ['pending', 'Pending'], ['approved', 'Approved'], ['rejected', 'Rejected'], ['converted', 'Converted']].map(([val, label]) => (
            <button key={val} className={`btn btn-sm ${filter === val ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilter(val)}>{label}</button>
          ))}
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}><Plus size={16} />New Requisition</button>
      </div>

      {loading ? (
        <div className="card" style={{ textAlign: 'center', padding: 60 }}><div className="loader loader-dark" style={{ margin: '0 auto' }} /></div>
      ) : reqs.length === 0 ? (
        <div className="card"><div className="empty-state"><ShoppingCart size={48} /><p>No requisitions found.</p></div></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {reqs.map((r: any) => (
            <div key={r.id} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{r.title} <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 12 }}>{r.requisition_number}</span></div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>{r.item_count} item(s) · {r.department_name || 'No department'} · Requested by {r.requested_by_first_name} {r.requested_by_last_name}</div>
                {r.needed_by_date && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Needed by {format(new Date(r.needed_by_date), 'd MMM yyyy')}</div>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className={`badge badge-${statusColor[r.status] || 'gray'}`} style={{ textTransform: 'capitalize' }}>{r.status}</span>
                {canManage && r.status === 'pending' && (
                  <>
                    <button className="btn btn-secondary btn-sm" onClick={() => act(r.id, 'approve')}><Check size={13} />Approve</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => act(r.id, 'reject')}><XIcon size={13} />Reject</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && <RequisitionModal onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); fetchReqs(); }} />}
    </div>
  );
}

function RequisitionModal({ onClose, onSaved }: any) {
  const [form, setForm] = useState<any>({ priority: 'normal' });
  const [items, setItems] = useState<any[]>([{ item_name: '', quantity: 1, estimated_unit_cost: '' }]);
  const [loading, setLoading] = useState(false);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validItems = items.filter(i => i.item_name && i.quantity);
    if (!form.title) { toast.error('Title is required'); return; }
    if (validItems.length === 0) { toast.error('At least one item is required'); return; }
    setLoading(true);
    try {
      await api.post('/procurement/requisitions', { ...form, items: validItems });
      toast.success('Requisition submitted');
      onSaved();
    } catch (err: any) { toast.error(err?.response?.data?.message || 'Failed to submit requisition'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>New Purchase Requisition</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)' }}>×</button></div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group"><label className="form-label">Title *</label><input className="form-input" required value={form.title || ''} onChange={e => set('title', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Justification</label><textarea className="form-input" rows={2} value={form.justification || ''} onChange={e => set('justification', e.target.value)} /></div>
            <div className="grid-2" style={{ gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Priority</label>
                <select className="form-input form-select" value={form.priority} onChange={e => set('priority', e.target.value)}>
                  <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
                </select>
              </div>
              <div className="form-group"><label className="form-label">Needed By</label><input type="date" className="form-input" value={form.needed_by_date || ''} onChange={e => set('needed_by_date', e.target.value)} /></div>
            </div>
            <ItemRows items={items} setItems={setItems} costField="estimated_unit_cost" />
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <span className="loader" /> : 'Submit Requisition'}</button></div>
        </form>
      </div>
    </div>
  );
}

function PurchaseOrders() {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [receiveModal, setReceiveModal] = useState<any>(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/procurement/purchase-orders', { params: filter ? { status: filter } : {} }); setOrders(r.data.data.purchase_orders || []); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const openReceive = async (po: any) => {
    const r = await api.get(`/procurement/purchase-orders/${po.id}`);
    setReceiveModal(r.data.data);
  };

  const downloadPdf = async (po: any) => {
    try {
      const r = await api.get(`/procurement/purchase-orders/${po.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch { toast.error('Failed to generate PDF'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[['', 'All'], ['draft', 'Draft'], ['sent', 'Sent'], ['partially_received', 'Partially Received'], ['received', 'Received'], ['cancelled', 'Cancelled']].map(([val, label]) => (
            <button key={val} className={`btn btn-sm ${filter === val ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setFilter(val)}>{label}</button>
          ))}
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}><Plus size={16} />New Purchase Order</button>
      </div>

      {loading ? (
        <div className="card" style={{ textAlign: 'center', padding: 60 }}><div className="loader loader-dark" style={{ margin: '0 auto' }} /></div>
      ) : orders.length === 0 ? (
        <div className="card"><div className="empty-state"><ShoppingCart size={48} /><p>No purchase orders found.</p></div></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {orders.map((po: any) => (
            <div key={po.id} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{po.po_number} — {po.vendor_name}</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>KES {Number(po.total_amount).toLocaleString()} · Ordered {format(new Date(po.order_date), 'd MMM yyyy')}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className={`badge badge-${statusColor[po.status] || 'gray'}`} style={{ textTransform: 'capitalize' }}>{po.status.replace('_', ' ')}</span>
                <button className="btn btn-secondary btn-sm" onClick={() => downloadPdf(po)}><FileDown size={13} />PDF</button>
                {['sent', 'partially_received'].includes(po.status) && (
                  <button className="btn btn-secondary btn-sm" onClick={() => openReceive(po)}><Truck size={13} />Receive Goods</button>
                )}
                {po.status === 'draft' && (
                  <button className="btn btn-secondary btn-sm" onClick={async () => { await api.patch(`/procurement/purchase-orders/${po.id}/status`, { status: 'sent' }); toast.success('PO marked sent'); fetchOrders(); }}>Mark Sent</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && <PurchaseOrderModal onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); fetchOrders(); }} />}
      {receiveModal && <ReceiveGoodsModal po={receiveModal} onClose={() => setReceiveModal(null)} onSaved={() => { setReceiveModal(null); fetchOrders(); }} />}
    </div>
  );
}

function PurchaseOrderModal({ onClose, onSaved }: any) {
  const [vendors, setVendors] = useState<any[]>([]);
  const [approvedRequisitions, setApprovedRequisitions] = useState<any[]>([]);
  const [form, setForm] = useState<any>({});
  const [items, setItems] = useState<any[]>([{ item_name: '', quantity: 1, unit_cost: '' }]);
  const [loading, setLoading] = useState(false);
  const [loadingRequisition, setLoadingRequisition] = useState(false);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get('/procurement/vendors').then(r => setVendors(r.data.data.vendors || []));
    api.get('/procurement/requisitions', { params: { status: 'approved' } }).then(r => setApprovedRequisitions(r.data.data.requisitions || []));
  }, []);

  const selectRequisition = async (requisitionId: string) => {
    set('requisition_id', requisitionId || undefined);
    if (!requisitionId) return;
    setLoadingRequisition(true);
    try {
      const r = await api.get(`/procurement/requisitions/${requisitionId}`);
      const requisitionItems = (r.data.data.items || []).map((i: any) => ({
        item_name: i.item_name, quantity: i.quantity, unit_cost: i.estimated_unit_cost || '',
      }));
      setItems(requisitionItems.length > 0 ? requisitionItems : [{ item_name: '', quantity: 1, unit_cost: '' }]);
      toast.success('Items prefilled from requisition — review unit costs before submitting');
    } catch { toast.error('Failed to load requisition items'); }
    finally { setLoadingRequisition(false); }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validItems = items.filter(i => i.item_name && i.quantity && i.unit_cost);
    if (!form.vendor_id) { toast.error('Select a vendor'); return; }
    if (validItems.length === 0) { toast.error('At least one item with a unit cost is required'); return; }
    setLoading(true);
    try {
      await api.post('/procurement/purchase-orders', { ...form, items: validItems });
      toast.success('Purchase order created');
      onSaved();
    } catch (err: any) { toast.error(err?.response?.data?.message || 'Failed to create purchase order'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>New Purchase Order</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)' }}>×</button></div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {approvedRequisitions.length > 0 && (
              <div className="form-group">
                <label className="form-label">Convert from Requisition (optional)</label>
                <select className="form-input form-select" value={form.requisition_id || ''} onChange={e => selectRequisition(e.target.value)} disabled={loadingRequisition}>
                  <option value="">Direct purchase order — no requisition</option>
                  {approvedRequisitions.map(r => (
                    <option key={r.id} value={r.id}>{r.requisition_number} — {r.title} ({r.item_count} item{r.item_count === 1 ? '' : 's'})</option>
                  ))}
                </select>
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Vendor *</label>
              <select className="form-input form-select" required value={form.vendor_id || ''} onChange={e => set('vendor_id', e.target.value)}>
                <option value="">Select vendor...</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Expected Delivery Date</label><input type="date" className="form-input" value={form.expected_delivery_date || ''} onChange={e => set('expected_delivery_date', e.target.value)} /></div>
            <ItemRows items={items} setItems={setItems} costField="unit_cost" />
            <div className="form-group"><label className="form-label">Notes</label><textarea className="form-input" rows={2} value={form.notes || ''} onChange={e => set('notes', e.target.value)} /></div>
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={loading || loadingRequisition}>{loading ? <span className="loader" /> : 'Create Purchase Order'}</button></div>
        </form>
      </div>
    </div>
  );
}

function ReceiveGoodsModal({ po, onClose, onSaved }: any) {
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    const items = Object.entries(quantities)
      .filter(([, qty]) => Number(qty) > 0)
      .map(([po_item_id, qty]) => ({ po_item_id, quantity_received: Number(qty) }));
    if (items.length === 0) { toast.error('Enter a received quantity for at least one item'); return; }
    setLoading(true);
    try {
      await api.post(`/procurement/purchase-orders/${po.id}/receive`, { items, notes });
      toast.success('Goods receipt recorded');
      onSaved();
    } catch (err: any) { toast.error(err?.response?.data?.message || 'Failed to record receipt'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>Receive Goods — {po.po_number}</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)' }}>×</button></div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {po.items?.map((it: any) => {
            const outstanding = it.quantity - it.quantity_received;
            return (
              <div key={it.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{it.item_name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Ordered {it.quantity} · Received {it.quantity_received} · Outstanding {outstanding}</div>
                </div>
                <input type="number" min={0} max={outstanding} className="form-input" placeholder="Qty received"
                  disabled={outstanding <= 0}
                  value={quantities[it.id] || ''}
                  onChange={e => setQuantities(q => ({ ...q, [it.id]: parseInt(e.target.value) || 0 }))} />
              </div>
            );
          })}
          <div className="form-group"><label className="form-label">Notes</label><textarea className="form-input" rows={2} value={notes} onChange={e => setNotes(e.target.value)} /></div>
        </div>
        <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" disabled={loading} onClick={submit}>{loading ? <span className="loader" /> : 'Record Receipt'}</button></div>
      </div>
    </div>
  );
}

function Vendors() {
  const [vendors, setVendors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState<any>(null);

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/procurement/vendors', { params: { search } }); setVendors(r.data.data.vendors || []); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(fetchVendors, 300);
    return () => clearTimeout(t);
  }, [fetchVendors]);

  const removeVendor = async (id: string) => {
    if (!window.confirm('Remove this vendor?')) return;
    try { await api.delete(`/procurement/vendors/${id}`); toast.success('Vendor removed'); fetchVendors(); }
    catch { toast.error('Failed to remove vendor'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 360 }}>
          <input className="form-input" placeholder="Search vendors..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal({})}><Plus size={16} />Add Vendor</button>
      </div>

      {loading ? (
        <div className="card" style={{ textAlign: 'center', padding: 60 }}><div className="loader loader-dark" style={{ margin: '0 auto' }} /></div>
      ) : vendors.length === 0 ? (
        <div className="card"><div className="empty-state"><ShoppingCart size={48} /><p>No vendors found.</p></div></div>
      ) : (
        <div className="grid-3">
          {vendors.map(v => (
            <div key={v.id} className="card">
              <h3 style={{ fontWeight: 700, fontSize: 15 }}>{v.name}</h3>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>{v.contact_person || 'No contact person'}</p>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                {v.category && <span className="badge badge-info">{v.category}</span>}
                {v.phone && <span className="badge badge-gray">{v.phone}</span>}
              </div>
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Total Spend</div>
                <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--success)' }}>KES {Number(v.total_spent || 0).toLocaleString()}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{v.order_count || 0} purchase order{v.order_count === 1 ? '' : 's'}</div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setShowModal(v)}><Edit2 size={13} />Edit</button>
                <button onClick={() => removeVendor(v.id)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)' }}><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && <VendorModal vendor={showModal} onClose={() => setShowModal(null)} onSaved={() => { setShowModal(null); fetchVendors(); }} />}
    </div>
  );
}

function VendorModal({ vendor, onClose, onSaved }: any) {
  const isEdit = !!vendor.id;
  const [form, setForm] = useState<any>({ ...vendor });
  const [loading, setLoading] = useState(false);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name) { toast.error('Vendor name is required'); return; }
    setLoading(true);
    try {
      if (isEdit) await api.put(`/procurement/vendors/${vendor.id}`, form);
      else await api.post('/procurement/vendors', form);
      toast.success(isEdit ? 'Vendor updated' : 'Vendor added');
      onSaved();
    } catch { toast.error('Failed to save vendor'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>{isEdit ? 'Edit Vendor' : 'Add Vendor'}</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)' }}>×</button></div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group"><label className="form-label">Name *</label><input className="form-input" required value={form.name || ''} onChange={e => set('name', e.target.value)} /></div>
            <div className="grid-2" style={{ gap: 12 }}>
              <div className="form-group"><label className="form-label">Contact Person</label><input className="form-input" value={form.contact_person || ''} onChange={e => set('contact_person', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Category</label><input className="form-input" value={form.category || ''} onChange={e => set('category', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Phone</label><input className="form-input" value={form.phone || ''} onChange={e => set('phone', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Email</label><input type="email" className="form-input" value={form.email || ''} onChange={e => set('email', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Tax PIN</label><input className="form-input" value={form.tax_pin || ''} onChange={e => set('tax_pin', e.target.value)} /></div>
            </div>
            <div className="form-group"><label className="form-label">Address</label><textarea className="form-input" rows={2} value={form.address || ''} onChange={e => set('address', e.target.value)} /></div>
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <span className="loader" /> : (isEdit ? 'Save Changes' : 'Add Vendor')}</button></div>
        </form>
      </div>
    </div>
  );
}
